import { computed, ref } from 'vue';

import { defineStore } from 'pinia';

import type { Depot, Group, Tenant } from '@/composables/api/userDepots/types';
import { useUserDepotsApi } from '@/composables/api/userDepots/useUserDepotsApi.ts';
import { sortByProp } from '@/utils/sort';

const useUserDepotsStore = defineStore('userDepots', () => {
  const { getMyDepots } = useUserDepotsApi();

  // State
  const depots = ref<Depot[]>([]);

  // Actions
  const loadDepots = async (): Promise<void> => {
    depots.value = await getMyDepots();
  };

  const getDepotsForDialog = (depotList: Depot[]): Tenant[] => {
    const sourceDepots = depotList || depots.value;

    // Single-pass: group depots by tenant, then by region within each tenant
    const tenantMap = new Map<
      string,
      {
        name: string;
        regionMap: Map<number, { group: Group; depots: Depot[] }>;
      }
    >();

    for (const depot of sourceDepots) {
      let tenantEntry = tenantMap.get(depot.tenant.id);
      if (!tenantEntry) {
        tenantEntry = { name: depot.tenant.name, regionMap: new Map() };
        tenantMap.set(depot.tenant.id, tenantEntry);
      }

      let regionEntry = tenantEntry.regionMap.get(depot.group.id);
      if (!regionEntry) {
        regionEntry = { group: { ...depot.group }, depots: [] };
        tenantEntry.regionMap.set(depot.group.id, regionEntry);
      }
      regionEntry.depots.push(depot);
    }

    // Build tenant list from the map
    const tenants: Tenant[] = [];
    for (const [id, { name, regionMap }] of tenantMap) {
      const tenant: Tenant = { id, name };

      if (regionMap.size > 1) {
        tenant.regions = [...regionMap.values()].map(
          ({ group, depots: regionDepots }) => ({
            ...group,
            depots: regionDepots.sort(sortByProp('externalDepotId'))
          })
        );
      } else {
        const onlyRegion = regionMap.values().next().value!;
        tenant.depots = onlyRegion.depots.sort(sortByProp('externalDepotId'));
      }

      tenants.push(tenant);
    }

    return tenants.sort(sortByProp('name'));
  };

  // Getters
  const getDepots = computed((): Depot[] => {
    return [...depots.value].sort(sortByProp('externalDepotId'));
  });

  const getFilterableDepotList = computed((): Depot[] => {
    return depots.value
      .map((depot) => {
        const { tenant, group, externalDepotId, locationName } = depot;
        const searchName = `${externalDepotId} ${locationName}`.toLowerCase();
        const sortName =
          `${tenant.name} ${group.name} ${searchName}`.toLowerCase();
        return { ...depot, sortName, searchName };
      })
      .sort(sortByProp('sortName'));
  });

  return {
    depots,
    loadDepots,
    getDepots,
    getFilterableDepotList,
    getDepotsForDialog
  };
});

export default useUserDepotsStore;
